import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PublicConfigurationContext } from "../../Context";
import { Alert, Badge, Card, CardBody, ChatMessageView, useLocalizedStrings } from "@aidkitorg/component-library";
import { ApplicantHeaderCard } from '../../DistroReview/ApplicantHeaderCard';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { usePost } from '../../API';
import { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import { Directory } from '@aidkitorg/aidkit/lib/directory';
import { getInteractionsFromConversationItems, Interaction } from '@aidkitorg/types/lib/convoai';
import { DEFAULT_AGENT_NAME, DEFAULT_AVATAR_URL, MessageContent } from '../AICoachUI';

type ApplicantConversationItemResult = extractReturnAsJson<Directory["/coach/conversation/applicant"]>[number];
type ScoreResult = extractReturnAsJson<Directory["/coach/conversation/scores"]>[number];

export function ApplicantConversationReviewPage(props: { uid: string }) {
  const config = useContext(PublicConfigurationContext);
  
  if (!config.capabilities?.aiCoach?.applicantConversationReview || config.capabilities?.aiCoach?.applicantConversationReview?.disabled) {    
    return <Alert variant="warning">Feature not enabled</Alert>
  }
  const { pinnedApplicantInfo, applicationState } = config.capabilities?.aiCoach?.applicantConversationReview;
      
  return <>
    <ApplicantHeaderCard
      uid={props.uid}
      pinnedApplicantInfo={pinnedApplicantInfo}
      applicationState={applicationState!}
      isCollapsed={false}
      onCollapseChange={() => { }}      
    />
    <div className="flex flex-col h-screen">
      <ApplicantConversations uid={props.uid} />
    </div>
  </>
}

function ApplicantConversations({ uid }: { uid: string }) {
  const fetchApplicantConversationItems = usePost("/coach/conversation/applicant");
  const fetchScores = usePost("/coach/conversation/scores");
  const [items, setItems] = useState<ApplicantConversationItemResult[]>([]);
  const [scores, setScores] = useState<ScoreResult[]>([]);
  const [loaded, setLoaded] = useState(false);
  const L = useLocalizedStrings();
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;

  useAsyncEffect(async function fetchConversationData() {
    if (!uid) return;
    let results = await fetchApplicantConversationItems({ applicantUid: uid });
    if (results && !("error" in results)) {
      // this is how we select for only coach related
      results = results.filter(r => r.application !== "docAssistance");
      setItems(results);
      const conversationIds = new Set<string>();
      for (const item of results) {
        conversationIds.add(item.conversationId);
      }
      const scoreResults = await fetchScores({ conversationIds: Array.from(conversationIds), includeAll: true });
      if (scoreResults && !("error" in scoreResults)) {
        setScores(scoreResults);
      }
    }
    setLoaded(true);
  }, [uid]);
  
  const interactionsByConversation: { [conversationId: string]: Interaction<ApplicantConversationItemResult>[] } = useMemo(() => {
    const itemsByConversation: { [conversationId: string]: ApplicantConversationItemResult[] } = {};
    for (const item of items) {
      itemsByConversation[item.conversationId] ??= [];
      itemsByConversation[item.conversationId].push(item);
    }
    return Object.fromEntries(
      Object.entries(itemsByConversation).map(([conversationId, items]) => [conversationId, getInteractionsFromConversationItems(items)])
    )
  }, [items]);

  if (loaded && Object.values(interactionsByConversation).flat().length === 0) {
    return <div className="p-6 text-sm text-gray-500">{L.ai_coach.dashboard.no_interactions}</div>;
  }

  return <>
    {
      Object.values(interactionsByConversation).flat().map((interaction, i) => { 
        const scoresForInteraction = scores.filter(
          score => (score.message_ids ?? []).some(
            messageId => [interaction.coachMessage, ...interaction.userMessages].map(message => message.id).includes(messageId)
          )
        );
        const conversationId = interaction.coachMessage?.conversationId;
        const overallScores = scores.filter(score => conversationId && score.conversation_id === conversationId && !score.message_ids?.length);
        return <div key={i} className="flex flex-col md:flex-row -mx-6 px-6 even:bg-gray-50">
          <ChatMessageView            
            transcript={
              [...interaction.userMessages, interaction.coachMessage].map(item => ({
                role: item.role,
                meta: { time: Date.now(), ...(item.meta ?? {}) },
                content: <Card expandable className="bg-transparent border-none">
                  <CardBody><MessageContent message={item} /></CardBody>
                </Card>
              }))
            }
            roles={{
              assistant: {
                side: 'left',
                color: 'bg-indigo-100',
                icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
              },
              user: {
                side: 'right',
                icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">U</div>,
                color: 'bg-gray-100',
              }
            }}
          />
          <ChatInteractionScoresAndFeedback
            interaction={interaction}
            scores={scoresForInteraction}
            overallScores={overallScores}
            className="flex-1 border-t md:border-l md:border-t-0" />
        </div>
      })    
    }
  </>
}

function ChatInteractionScoresAndFeedback(props: {
  interaction: Interaction,
  scores: ScoreResult[],
  overallScores: ScoreResult[],
  className?: string
}) { 
  const L = useLocalizedStrings();
  const feedbackItem = props.interaction.coachMessage?.meta?.feedback;
  const allScores = [...props.scores, ...props.overallScores];
  
    
  return <div className={`${props.className} flex-col p-4`}>
    <ul className="flex flex-col gap-4">
      {props.interaction.appState?.appState?.currentSection && <li>
        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
          {L.ai_coach.dashboard.app_section}:
        </div>
        <div className="text-sm">{props.interaction.appState?.appState?.currentSection?.name}</div>
      </li>}
      
      {!!feedbackItem && <li>
        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
          {L.ai_coach.dashboard.applicant_feedback}:
        </div>
        <div className="text-sm flex-col gap-2 items-center">
          <div>{ feedbackItem.rating === "up" ? "👍" : (feedbackItem.rating === "down") ? "👎" : "Unknown" }</div>
          {feedbackItem.reason && <div>
            <span className="font-bold">{L.ai_coach.dashboard.reasoning}</span>: {(feedbackItem.reason.toLowerCase() === "other" && feedbackItem.additionalFeedback) ? `"${feedbackItem.additionalFeedback}"` : feedbackItem.reason}
          </div>}
        </div>
      </li>}
      
      {(allScores.length > 0) && <li>
        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
          {L.ai_coach.dashboard.scores}:
        </div>
        <div className="flex flex-col gap-2 divide-y">
          {/* individual interaction scores */}
          {allScores.map((score, i) => {
            const pass = score.value === 1
            return <div className="text-sm flex-col gap-2 items-center py-2" key={i}>
              <div className="flex gap-1 items-center">
                {!score.message_ids?.length && <div className="font-bold">{ L.ai_coach.dashboard.overall_scores }:</div>}
                <Badge variant={pass ? "success" : "error"}>{pass ? L.ai_coach.dashboard.pass : L.ai_coach.dashboard.fail}</Badge>
              </div>
              {score.reasoning && <div><span className="font-bold">{L.ai_coach.dashboard.reasoning}</span>: {score.reasoning}</div>}
            </div>;
          })}          
        </div>
      </li>}
      
      
    </ul>
    
  </div>
}
